import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { DiagnosisDto, DiagnosisSearchRequest, UpsertDiagnosisDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import { deleteDiagnosis, findDiagnosisById, searchDiagnoses } from 'services/diagnosisServices';
import UpsertDiagnosisForm from 'components/settings/diagnosis/UpsertDiagnosisForm';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';
import CheckBox from 'components/final-form/CheckBox';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';

const initialDiagnosisSearch: Partial<DiagnosisSearchRequest> = {
  includesInactive: false
}
interface Props extends MessageSourceProps, AuthConsumerRenderProps{
}

interface State {
  foundDiagnoses: Array<DiagnosisDto>,
  diagnosesLoaded: boolean,
  diagnosis?: UpsertDiagnosisDto,
  diagnosisId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValue: Partial<DiagnosisSearchRequest>,
  cancelTokenSource: CancelTokenSource,
  isDefault: boolean,
}

interface DiagnosisFieldProps extends FormState {
  values: Partial<DiagnosisSearchRequest>
}


class DiagnosisView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundDiagnoses: [],
      diagnosesLoaded: false,
      diagnosis: undefined,
      diagnosisId: undefined,
      addEditModalOpen: false,
      loading: false,
      searchValue: initialDiagnosisSearch,
      cancelTokenSource,
      isDefault: false
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  };

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;
    if (id) {
      findDiagnosisById(id, cancelTokenSource)
          .then(response => {
            this.showModal(id, response);
          })
          .catch(noop);
    }
  };

  showModal = (id?: number, diagnosis?: UpsertDiagnosisDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        diagnosis: diagnosis,
        diagnosisId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      diagnosisId: undefined,
      diagnosis: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValue, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        diagnosesLoaded: true
      })
    };

    searchDiagnoses(searchValue, cancelTokenSource)
        .then(response => {
          this.setState({
            foundDiagnoses: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteDiagnosis = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteDiagnosis(id, cancelTokenSource).then(response => {
      this.setState(({foundDiagnoses}: State) => ({
        foundDiagnoses: foundDiagnoses.filter((diagnosis: DiagnosisDto) => diagnosis.id != response.id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: DiagnosisFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({diagnosesLoaded: false});

    const onFinally = () => {
      this.setState({
        diagnosesLoaded: true
      })
    };

    searchDiagnoses(values, cancelTokenSource)
        .then(response => {
          this.setState({
            foundDiagnoses: response,
            searchValue: values
          });
        })
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {diagnosesLoaded} = this.state;
    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchDiagnosisSegment()}
          </div>

          <StyledViewTableRow>
            {!diagnosesLoaded ? this.renderLoader() : this.renderDiagnosisSegment()}
          </StyledViewTableRow>

          {this.renderUpsertDiagnosisModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchDiagnosisSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.state.searchValue}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundDiagnoses} = this.state;

    const numResults = foundDiagnoses.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="diagnosisSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"middle"}>
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("diagnosis.loading")}</Loader>
        </Dimmer>
    );
  };

  renderDiagnosisSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundDiagnoses} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundDiagnoses.length}
            rowGetter={this.diagnosisRowGetter}
            rowRenderer={this.foundDiagnosesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("diagnosis.code")),
                dataKey: "code"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("diagnosis.description")),
                dataKey: "description",
                cellRenderer: (this.descriptionCell)
              },
              {
                width: 300,
                label: (getMessage("diagnosis.status")),
                dataKey: "active",
                cellRenderer: (this.statusCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  diagnosisRowGetter = ({index}: any) => {
    const {foundDiagnoses} = this.state;

    Object.assign(foundDiagnoses[index], {index: index + 1});

    return foundDiagnoses[index]
  };

  foundDiagnosesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundDiagnoses} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundDiagnoses[index].id)}
        >
          {columns}
        </div>
    );
  };

  descriptionCell = ({rowData}: any) => {
    const {language} = this.props;

    let defaultDescription = rowData.description;


    const texts: { [key: string]: string } = {
      'de': defaultDescription,
      'en': rowData.descriptionEn,
      'fr': rowData.descriptionFr,
      'it': rowData.descriptionIt,
    };

    let description = texts[language];

    return (
        <div>
          {description}
        </div>
    )
  }

  statusCellRenderer = ({rowData}: any) => {
    const {getMessage} = this.props;
    let status = '';

    if(rowData.active === true) {
      status = getMessage('diagnosis.status.Active');
    } else {
      status = getMessage('diagnosis.status.Inactive');
    }
    return (
        <div>
          {status}
        </div>
    )
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("diagnosis.confirmDelete")}
              onConfirm={() => this.deleteDiagnosis(cellData)}
          />
        </React.Fragment>
    );
  };

  renderUpsertDiagnosisModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, diagnosis, diagnosisId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("diagnosis")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertDiagnosisForm
                diagnosis={diagnosis}
                diagnosisId={diagnosisId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

}

export default withAuthContext(withMessages(DiagnosisView));
